<template>
  <base-modal
    :title="`${title}.${extension}`"
    :width="400"
    @closeModal="onCloseModal"
  >
    <audio-player
      :src="audioUrl"
      :volume-control="false"
      :volume="asset.volume"
    />
    <div class="form">
      <div class="header">PLAY MODE SETTINGS</div>
      <div class="fields">
        <div class="field-radio" :class="{ checked: mode === 'once' }">
          <input
            id="clip-once"
            v-model="mode"
            type="radio"
            name="once"
            value="once"
            @change="onModeChange"
          />
          <label for="clip-once">
            <svg-icon
              icon="app-icons/audio-start"
              width="20"
              height="20"
              :fill="mode === 'once' ? '#FFFFFF' : '#BCBCBC'"
            />
            Play clip once
          </label>
        </div>
        <div class="field-radio" :class="{ checked: mode === 'loop' }">
          <input
            id="clip-loop"
            v-model="mode"
            type="radio"
            value="loop"
            @change="onModeChange"
          />
          <label for="clip-loop">
            <svg-icon
              icon="app-icons/audio-loop"
              width="20"
              height="20"
              :fill="mode === 'loop' ? '#FFFFFF' : '#BCBCBC'"
            />
            Loop clip indefinitely
          </label>
        </div>
        <div class="field-radio" :class="{ checked: mode === 'stop' }">
          <input
            id="clip-stop"
            v-model="mode"
            type="radio"
            name="stop"
            value="stop"
            @change="onModeChange"
          />
          <label for="clip-stop">
            <svg-icon
              icon="app-icons/audio-stop"
              width="20"
              height="20"
              :fill="mode === 'stop' ? '#FFFFFF' : '#BCBCBC'"
            />
            Stop playing all instances of clip
          </label>
        </div>
        <div class="delay" :class="{ disabled: mode === 'stop' }">
          Delay
          <input
            ref="delayInput"
            v-model="delay"
            type="number"
            :disabled="mode === 'stop'"
          />
          ms
        </div>
      </div>
    </div>
  </base-modal>
</template>

<script>
import _ from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import SvgIcon from '../all/SvgIcon.vue';
import BaseModal from './BaseModal.vue';
import AudioPlayer from '../all/AudioPlayer.vue';

export default {
  components: {
    BaseModal,
    AudioPlayer,
    SvgIcon,
  },

  data() {
    return {
      mode: '',
      delay: 0,
      elementId: null,
      index: null,
    };
  },

  computed: {
    ...mapGetters('project/data/assets', [
      'getAudioUrl',
      'getNode',
      'getExtension',
    ]),
    ...mapGetters('project/data/elements', ['getAssets']),
    ...mapGetters('application/modals', ['getAudioAssetRefModal']),

    /**
     * Returns the ID of the asset
     */
    id() {
      const { id } = this.getAudioAssetRefModal;
      return id;
    },

    /**
     * Returns the Asset node
     */
    asset() {
      return this.getNode(this.id);
    },

    assetRef() {
      return this.getAssets(this.elementId).audio[this.index];
    },

    /**
     * Returns the audio file URL
     */
    audioUrl() {
      if (!this.getAudioAssetRefModal) return null;
      return this.getAudioUrl(this.id);
    },

    /**
     * Returns the title of the asset
     */
    title() {
      return this.asset.name;
    },

    /**
     * Returns the extension of the asset file
     */
    extension() {
      return this.getExtension(this.id);
    },
  },

  mounted() {
    this.$el.focus();
    const { elementId, index } = this.getAudioAssetRefModal;
    this.elementId = elementId;
    this.index = index;

    this.mode = this.assetRef.mode;
    this.delay = this.assetRef.delay;

    this.$refs.delayInput.addEventListener(
      'change',
      _.debounce(this.onDelayChange, 500)
    );
  },

  methods: {
    ...mapActions('application/modals', ['setAudioAssetRefModal']),
    ...mapActions('project/data/elements', ['updateAudio']),

    onCloseModal() {
      this.setAudioAssetRefModal({ value: null });
    },

    onModeChange(event) {
      this.updateAudio({
        id: this.elementId,
        index: this.index,
        props: { mode: this.mode, delay: this.delay },
        apply: true,
      });
    },

    onDelayChange(event) {
      this.updateAudio({
        id: this.elementId,
        index: this.index,
        props: { mode: this.mode, delay: this.delay },
        apply: true,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.form {
  margin: 0 30px;

  .header {
    opacity: 0.5;
  }
}
.fields {
  display: flex;
  padding: 20px 0;
  flex-direction: column;
  gap: 15px;

  .field-radio {
    transition: color 0.25s;

    &.checked,
    &:hover {
      color: #ffffff;
    }
  }
}

[type='radio']:checked,
[type='radio']:not(:checked) {
  position: absolute;
  display: none;
}
[type='radio']:checked + label,
[type='radio']:not(:checked) + label {
  position: relative;
  cursor: pointer;
  padding: 0;
  margin: 0 25px 15px 30px;
  font-size: 14px;
  display: flex;
  gap: 10px;
}
[type='radio']:checked + label:before,
[type='radio']:not(:checked) + label:before {
  content: '';
  position: absolute;
  left: -30px;
  top: 50%;
  margin-top: -9px;
  width: 20px;
  height: 20px;
  border: 1px solid #696d6d;
  border-radius: 100%;
  background: #252929;
}
[type='radio']:checked + label:after,
[type='radio']:not(:checked) + label:after {
  content: '';
  width: 12px;
  height: 12px;
  background: #db841e;
  position: absolute;
  top: 50%;
  margin-top: -5px;
  left: -26px;
  border-radius: 100%;
  -webkit-transition: all 0.2s ease;
  transition: all 0.2s ease;
}
[type='radio']:not(:checked) + label:after {
  opacity: 0;
  -webkit-transform: scale(0);
  transform: scale(0);
}
[type='radio']:checked + label:after {
  opacity: 1;
  -webkit-transform: scale(1);
  transform: scale(1);
}

[type='radio']:not(:checked) + label[disabled] {
  cursor: default;
  &:before {
    border: 1px solid #686b6b;
    background: #313536;
  }
  .text {
    color: #686b6b;
  }
}

input[type='number'] {
  padding: 5px;
  background: #252929;
  border: none;
  border-radius: 4px;
  margin: 0 8px;
  color: #bcbcbc;
  width: 50px;
  overflow: hidden;
  font-family: 'Open sans';
  font-size: 15px;
  width: 90px;

  &:disabled {
    color: #666666;
  }
}

.delay.disabled {
  color: #666666;
}
</style>
